import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import React, { FC, SyntheticEvent, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import ModalBox from '../../../../common/modal/modal-box';
import { TelegramLeadStatus } from '../../../../interfaces/common';
import { BotContext } from '../../../bot.context';
import { ITelegramChannelWithActivators } from '../../../channels/interfaces';
import {
  ConditionsFlowNodeConditionData,
  ConditionTypes,
} from '../../interfaces';

import ConditionsWrapperModal from './conditions-wrapper.modal';

type ConditionsModalProps = {
  creatingCondition: ConditionsFlowNodeConditionData | null;
  open: boolean;
  onClose: () => void;
  addToConditions: () => void;
  onChangeCreatingCondition: (data: ConditionsFlowNodeConditionData) => void;
  onSelectChannel: (event: SelectChangeEvent) => void;
  onSelectStatus: (event: SelectChangeEvent) => void;
  channels: ITelegramChannelWithActivators[];
};

const ConditionsModal: FC<ConditionsModalProps> = ({
  creatingCondition,
  open,
  onClose,
  addToConditions,
  onChangeCreatingCondition,
  onSelectChannel,
  channels,
  onSelectStatus,
}) => {
  const { bot } = useContext(BotContext);

  const navigate = useNavigate();
  return (
    <ModalBox
      open={open}
      onClose={onClose}
      maxWidth="400px"
      sx={{
        height: '220px',
        p: '24px 16px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'grey.10',
        borderRadius: '12px',
        width: '400px',
        position: 'relative',
      }}
    >
      <Button
        onClick={onClose}
        sx={{
          width: '24px',
          minWidth: '24px',
          height: '24px',
          position: 'absolute',
          top: '5px',
          right: '5px',
          backgroundColor: 'transparent',
        }}
      >
        <CloseIcon sx={{ fontSize: '18px', color: 'grey.1', opacity: '0.6' }} />
      </Button>
      {creatingCondition &&
        (() => {
          switch (creatingCondition.type) {
            case ConditionTypes.checkChannelMembership: {
              return (
                <ConditionsWrapperModal
                  title={t('flowNodes.checkIfInChanel')}
                  addToConditions={addToConditions}
                  onClose={onClose}
                  disabledButton={Boolean(!creatingCondition.channelId)}
                >
                  <FormControl fullWidth>
                    <InputLabel>{t('common.channel')}</InputLabel>
                    <Select
                      value={creatingCondition?.channelId ?? ''}
                      label="Channel"
                      onChange={(event) => {
                        onSelectChannel(event);
                      }}
                      sx={{
                        '.MuiSelect-select': {
                          backgroundColor: 'grey.15',
                          py: '14px',
                        },
                      }}
                    >
                      {channels.map(({ title, _id }) => (
                        <MenuItem key={_id} value={_id}>
                          {title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </ConditionsWrapperModal>
              );
            }

            case ConditionTypes.checkParam:
              return (
                <ConditionsWrapperModal
                  title={t('flowNodes.checkParam')}
                  addToConditions={addToConditions}
                  onClose={onClose}
                  disabledButton={Boolean(
                    !creatingCondition.key || !creatingCondition.value,
                  )}
                >
                  <Autocomplete
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-root': {
                        backgroundColor: 'grey.15',
                        height: '56px',
                        p: '0',
                        pl: '12px',
                      },
                    }}
                    options={bot.params}
                    value={creatingCondition.key}
                    onInputChange={(event, newInputValue) => {
                      onChangeCreatingCondition({
                        ...creatingCondition,
                        inputValue: newInputValue ?? ' ',
                      });
                    }}
                    onChange={(e: SyntheticEvent, newValue: string | null) => {
                      onChangeCreatingCondition({
                        ...creatingCondition,
                        inputValue: newValue ?? '',
                        key: newValue ?? '',
                      });
                    }}
                    inputValue={creatingCondition.inputValue || ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('common.key')}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontWeight: 500,
                            fontSize: '18px',
                          },
                        }}
                      />
                    )}
                    noOptionsText={
                      <Stack sx={{ textAlign: 'center' }}>
                        <Typography
                          sx={{ mt: '15px', fontWeight: 500, fontSize: '14px' }}
                        >
                          {t('common.notFoundVariable')}
                        </Typography>
                        <Button
                          onClick={() =>
                            navigate(
                              `/project/${bot?.project}/bot/${bot?._id}/variables`,
                            )
                          }
                          sx={{ mt: '15px', fontSize: '15px' }}
                        >
                          {t('common.createVariable')}
                        </Button>
                      </Stack>
                    }
                  />
                  <TextField
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-root': {
                        height: '56px',
                        fontWeight: 500,
                        fontSize: '18px',
                      },
                    }}
                    placeholder={t('common.value')}
                    value={creatingCondition.value}
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      if (value.length > 20) return;

                      onChangeCreatingCondition({
                        ...creatingCondition,
                        value,
                      });
                    }}
                  />
                </ConditionsWrapperModal>
              );

            case ConditionTypes.checkStatus:
              return (
                <ConditionsWrapperModal
                  title={t('flowNodes.checkStatus')}
                  addToConditions={addToConditions}
                  onClose={onClose}
                  disabledButton={Boolean(!creatingCondition?.status)}
                >
                  <Box sx={{ width: '100%' }}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={creatingCondition?.status ?? ''}
                        label="Status"
                        onChange={(event) => {
                          onSelectStatus(event);
                        }}
                        sx={{
                          '.MuiSelect-select': {
                            backgroundColor: 'grey.15',
                            py: '14px',
                          },
                        }}
                      >
                        <MenuItem
                          key={'status_lead'}
                          value={TelegramLeadStatus.lead}
                        >
                          {TelegramLeadStatus.lead}
                        </MenuItem>
                        <MenuItem
                          key={'status_reg'}
                          value={TelegramLeadStatus.reg}
                        >
                          {TelegramLeadStatus.reg}
                        </MenuItem>
                        <MenuItem
                          key={'status_dep'}
                          value={TelegramLeadStatus.dep}
                        >
                          {TelegramLeadStatus.dep}
                        </MenuItem>
                        <MenuItem
                          key={'status_qual'}
                          value={TelegramLeadStatus.qual}
                        >
                          {TelegramLeadStatus.qual}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </ConditionsWrapperModal>
              );
          }
        })()}
    </ModalBox>
  );
};
export default ConditionsModal;
